import { useMemo } from 'react';
import { IIncomeFloorEstimate } from 'views/ProFormaTable/types';

interface FloorAllocationData {
    total: number;
    rows: IIncomeFloorEstimate[];
}

interface FloorAllocationResult {
    floorAllocations: Record<number, FloorAllocationData>;
    incompleteAllocationFloors: number[];
    isLastRowOfFloor: (rowId: string, floor: number) => boolean;
    hasAllFloors: boolean;
}

export const useFloorAllocations = (floorEstimates: IIncomeFloorEstimate[] = [], averageStories: number | null): FloorAllocationResult => useMemo(() => {
    // Group floor estimates by floor number and calculate if allocations sum to 1 (100%)
    const floorAllocations = floorEstimates.reduce((acc, estimate) => {
        const { floor } = estimate;
        if (!acc[floor]) {
            acc[floor] = {
                total: 0,
                rows: [],
            };
        }
        acc[floor].total += estimate.allocationPercentage || 0;
        acc[floor].rows.push(estimate);
        return acc;
    }, {} as Record<number, FloorAllocationData>);

    // Find floors that don't sum to 1 (100%)
    const incompleteAllocationFloors = Object.entries(floorAllocations)
        .filter(([, data]) => Math.abs(data.total - 1) > 0.0001) // Using 0.0001 tolerance for floating point comparison
        .map(([floor]) => Number(floor));

    const isLastRowOfFloor = (rowId: string, floor: number): boolean => {
        const floorRows = floorAllocations[floor]?.rows || [];
        const lastRow = floorRows[floorRows.length - 1];
        return lastRow && String(lastRow.id) === rowId;
    };

    // Check if we have at least one floor for each story level
    const existingFloors = new Set(Object.keys(floorAllocations).map(Number));
    const hasAllFloors = averageStories
        ? Array.from({ length: Math.round(averageStories) }, (_, i) => i + 1)
            .every(floor => existingFloors.has(floor))
        : false;

    return {
        floorAllocations, incompleteAllocationFloors, isLastRowOfFloor, hasAllFloors,
    };
}, [floorEstimates, averageStories]);
